import { useState, useEffect } from 'react'
import { X, Flame, Camera, Link2, Trophy, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react'

const ONBOARDING_KEY = 'quest_onboarding_complete'

interface OnboardingStep {
  icon: React.ReactNode
  title: string
  description: string
  tip: string
}

const steps: OnboardingStep[] = [
  {
    icon: <Sparkles className="w-8 h-8 text-quest-primary" />,
    title: 'Welcome to Quest!',
    description: 'Quest transforms your schoolwork into an adventure. Complete assignments, earn XP, level up, and unlock achievements!',
    tip: 'Your progress syncs from Canvas automatically.',
  },
  {
    icon: <Camera className="w-8 h-8 text-blue-400" />,
    title: 'Scan Your Work',
    description: 'Take photos of your completed homework, tests, and assignments. Each scan you submit earns XP!',
    tip: 'Pro tip: Scan your work right after finishing it.',
  },
  {
    icon: <Link2 className="w-8 h-8 text-green-400" />,
    title: 'Match for Bonus XP',
    description: 'Connect your scans to specific assignments from Canvas. Matching earns you +15 bonus XP per item!',
    tip: 'Check the "Match Your Work" section on your dashboard.',
  },
  {
    icon: <Flame className="w-8 h-8 text-orange-500" />,
    title: 'Build Your Streak',
    description: 'Log in and study every day to build your streak. Longer streaks mean bigger XP multipliers!',
    tip: 'A 7-day streak gives you a 1.5x XP bonus.',
  },
  {
    icon: <Trophy className="w-8 h-8 text-purple-400" />,
    title: 'Unlock Achievements',
    description: 'Complete challenges and hit milestones to unlock special achievements. Can you collect them all?',
    tip: 'Check your Achievements page to see what you can unlock.',
  },
]

export default function OnboardingModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)

  useEffect(() => {
    // Check if onboarding has been completed
    const completed = localStorage.getItem(ONBOARDING_KEY)
    if (!completed) {
      setIsOpen(true)
    }
  }, [])

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true')
    setIsOpen(false)
  }

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true')
    setIsOpen(false)
  }

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      handleComplete()
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  if (!isOpen) return null

  const step = steps[currentStep]

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-card rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="relative h-2 bg-gray-700">
          <div
            className="h-full bg-quest-primary transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-white p-2"
          title="Skip onboarding"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center mx-auto mb-6">
            {step.icon}
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold mb-3">{step.title}</h2>

          {/* Description */}
          <p className="text-gray-300 mb-4">{step.description}</p>

          {/* Tip */}
          <div className="bg-gray-700/50 rounded-lg p-3 text-sm text-gray-400">
            <span className="text-quest-primary font-medium">Tip:</span> {step.tip}
          </div>
        </div>

        {/* Navigation */}
        <div className="px-8 pb-8 flex items-center justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`flex items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
              currentStep === 0
                ? 'text-gray-600 cursor-not-allowed'
                : 'text-gray-400 hover:text-white hover:bg-surface-hover'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {/* Step indicators */}
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep ? 'bg-quest-primary' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextStep}
            className="flex items-center gap-1 px-4 py-2 bg-quest-primary hover:bg-quest-primary/80 rounded-lg font-medium transition-colors"
          >
            {currentStep === steps.length - 1 ? (
              "Let's Go!"
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
